import React, { useEffect, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { Chart, ArcElement, LineElement, LineController } from 'chart.js/auto'
import moment from 'moment'

interface GraphDetail {
  date: string
  revenue: number
  payouts: number
  sales: number
}

interface Props {
  graphiDetails: GraphDetail[]
  startDate: string
  endDate: string
}

const LineChart = ({ graphiDetails, startDate, endDate }: Props) => {
  const [dates, setDates] = useState<string[]>([])
  const [revenue, setRevenue] = useState<number[]>([])
  const [payout, setPayout] = useState<number[]>([])
  const [sales, setSales] = useState<number[]>([])
  Chart.register(ArcElement, LineElement, LineController)

  const getSundays = (finalStartDate: any, finalEndDate: any) => {
    let currentDate = new Date(finalStartDate)
    let dayOne = new Date(finalStartDate)
    const sundays = []

    if (dayOne.getDay() !== 0) {
      const firstDateFormatted = moment(finalStartDate).format('MMM, DD')
      sundays.push(firstDateFormatted)
    }

    while (currentDate <= finalEndDate) {
      if (currentDate.getDay() === 0) {
        sundays.push(
          moment(currentDate, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ').format(
            'MMM, DD'
          )
        )
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }
    return sundays
  }

  const handleShortTermData = (details: GraphDetail[]) => {
    if (graphiDetails?.length < 30) {
      const formattedDates = details.map((obj) => {
        const parsedDate = moment(obj.date, ['MM-YYYY', 'DD-MM-YYYY'], true)
        return parsedDate.isValid()
          ? parsedDate.format(
              parsedDate.creationData().format === 'MM-YYYY'
                ? 'MMM YY'
                : 'MMM DD'
            )
          : ''
      })

      setDates(formattedDates)
    } else if (graphiDetails?.length === 30) {
      const formattedDates = details.map((obj) => obj.date)
      setDates(formattedDates)
    }
    setRevenue(details.map((obj) => obj.revenue))
    setPayout(details.map((obj) => obj.payouts))
    setSales(details.map((obj) => obj.sales))
  }

  const handleMediumTermData = (
    details: GraphDetail[],
    start: string,
    end: string
  ) => {
    const finalStartDate = new Date(start)
    const finalEndDate = new Date(end)

    const sundays = getSundays(finalStartDate, finalEndDate)
    setDates(sundays)

    const dateObjects = details.map((dateObj) => ({
      ...dateObj,
      day: getDayOfWeek(dateObj.date),
    }))

    aggregateWeeklyData(dateObjects)
  }

  const getDayOfWeek = (dateString: string): string => {
    const date = new Date(
      moment(dateString, ['DD-MM-YYYY', 'MM-YYYY']).toDate()
    )
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return days[date.getUTCDay()]
  }

  const aggregateWeeklyData = (dateObjects: GraphDetail[]) => {
    let revenueAcc = 0
    let payoutAcc = 0
    let salesAcc = 0

    const revenueArray: number[] = []
    const payoutArray: number[] = []
    const salesArray: number[] = []

    dateObjects.forEach((data: any, index: number) => {
      revenueAcc += data.revenue
      payoutAcc += data.payouts
      salesAcc += data.sales

      if (data?.day === 'Sat' || index === dateObjects.length - 1) {
        revenueArray.push(revenueAcc)
        payoutArray.push(payoutAcc)
        salesArray.push(salesAcc)
        revenueAcc = 0
        payoutAcc = 0
        salesAcc = 0
      }
    })
    setRevenue(revenueArray)
    setPayout(payoutArray)
    setSales(salesArray)
  }

  useEffect(() => {
    if (graphiDetails?.length <= 30) {
      handleShortTermData(graphiDetails)
    } else if (graphiDetails?.length >= 31 && graphiDetails?.length <= 365) {
      handleMediumTermData(graphiDetails, startDate, endDate)
    }
  }, [graphiDetails])

  const labels = dates

  const data = {
    labels: labels,
    datasets: [
      {
        label: 'Revenue',
        backgroundColor: '#EDB84F',
        borderColor: '#EDB84F',
        data: revenue,
        fill: false,
        tension: 0.1,
      },
      {
        label: 'Payouts',
        backgroundColor: '#2B91C9',
        borderColor: '#2B91C9',
        data: payout,
      },
      {
        label: 'No. Of Sales',
        backgroundColor: '#5A4BDA',
        borderColor: '#5A4BDA',
        data: sales,
      },
    ],
  }

  const isSunday = (dateString: any) =>
    moment(dateString, 'DD-MM-YYYY').day() === 0

  return (
    <Line
      options={{
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: { usePointStyle: true },
          },
        },
        scales: {
          x: {
            grid: {
              display: true,
              color: '#e5e7eb',
            },
            ticks: {
              color: '#3D3D3D',
              padding: 10,
              maxTicksLimit: graphiDetails?.length === 30 ? 7 : 14,
              callback: (value: any, index: any, tick) => {
                if (graphiDetails?.length === 30) {
                  if (index === 0 && !isSunday(dates[value])) {
                    const formattedDate = moment(
                      dates[value],
                      'DD-MM-YYYY'
                    ).format(' MMM DD')
                    return formattedDate
                  } else if (isSunday(dates[value])) {
                    const formattedDate = moment(
                      dates[value],
                      'DD-MM-YYYY'
                    ).format(' MMM DD')
                    return formattedDate
                  }
                } else {
                  return dates[value]
                }
              },
            },
          },
          y: {
            border: {
              color: '#e5e7eb',
              dash: [2, 2],
              display: false,
            },
            grid: {
              display: false,
              color: '#e5e7eb',
            },
            ticks: {
              display: false,
            },
          },
        },
      }}
      data={data}
    />
  )
}

export default LineChart
